import { Button } from "@circleco/compass/components/Button";
import { Modal } from "@circleco/compass/components/Modal";
import { TextArea } from "@circleco/compass/components/TextArea";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useRef } from "react";

import { t } from "@/i18n-js/instance";
import type { ModerationActionOptions } from "@/react/components/SettingsApp/Moderation/useModerationApi";
import { ModerationDetailContent } from "@/react/components/shared/Moderation/ModerationDetailContent";
import { ReportsAccordionPanel } from "@/react/components/shared/Moderation/ReportsAccordionPanel";
import { settingsNavPathV3 } from "@/react/helpers/urlHelpers/settingsNavPathV3";
import {
  CHAT_ROOM_MESSAGE_TYPE,
  CONNECT_CONNECTION_TYPE,
  COMMENT_TYPE,
} from "@/react/types/FlaggedContent";

import { useApproveRejectContent } from "../useApproveRejectContent";
import { useModerationFooterActions } from "./useModerationFooterActions";

const i18nRoot = "settings.moderation.review_flagged_content_modal";

const REVIEW_TITLE_KEYS: Record<string, string> = {
  [CHAT_ROOM_MESSAGE_TYPE]: "message_review_title",
  [COMMENT_TYPE]: "comment_review_title",
  [CONNECT_CONNECTION_TYPE]: "connection_review_title",
};

const getReviewTitle = (type: string) =>
  t([i18nRoot, REVIEW_TITLE_KEYS[type] ?? "post_review_title"]);

export interface ReportFlaggedContentModalComponentProps {
  flaggedContentId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  shouldShowGoToModerationButton?: boolean;
}

export const ReportFlaggedContentModalComponent = ({
  flaggedContentId,
  shouldShowGoToModerationButton = false,
  open,
  onOpenChange,
}: ReportFlaggedContentModalComponentProps) => {
  const {
    approveFlaggedContent,
    rejectFlaggedContent,
    flaggedContent,
    isApproving,
    isRejecting,
  } = useApproveRejectContent(flaggedContentId);
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false });

  const {
    author_is_flagged: isAuthorFlagged,
    flagged_contentable,
    id: currentReportId,
    initiator_is_flagged_for_connect: isFlaggedForConnect,
    member_connection_reports_count: memberConnectionReportsCount = 0,
    reports = [],
    status,
  } = flaggedContent ?? {};

  const type = flagged_contentable?.type;
  const isConnection = type === CONNECT_CONNECTION_TYPE;
  const isChatMessage = type === CHAT_ROOM_MESSAGE_TYPE;
  const reportsCount = isConnection
    ? memberConnectionReportsCount
    : reports.length;

  const handleOpenChangeRef = useRef<(open: boolean) => void>(onOpenChange);

  const onApprove = useCallback(
    (options?: ModerationActionOptions) => {
      approveFlaggedContent({ options });
      handleOpenChangeRef.current(false);
    },
    [approveFlaggedContent],
  );

  const onReject = useCallback(
    (reason: string | null, options?: ModerationActionOptions) => {
      rejectFlaggedContent({ reason, options });
      handleOpenChangeRef.current(false);
    },
    [rejectFlaggedContent],
  );

  const {
    rejectState,
    setRejectState,
    handleRejectWithOptions,
    handleRejectWithReason,
    rejectMenuOptions,
    approveMenuOptions,
    rejectLabel,
    resetRejectState,
  } = useModerationFooterActions({
    flaggedContentId,
    isAuthorFlagged,
    isFlaggedForConnect,
    isEmbeddedChat: isChatMessage && !!flagged_contentable?.is_embedded,
    status,
    type,
    onApprove,
    onReject,
  });

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        if (searchParams.flagged_content_id) {
          void navigate({
            to: ".",
            search: prev => ({ ...prev, flagged_content_id: undefined }),
          });
        }
        resetRejectState();
      }
      onOpenChange(newOpen);
    },
    [searchParams.flagged_content_id, navigate, resetRejectState, onOpenChange],
  );
  handleOpenChangeRef.current = handleOpenChange;

  if (!flaggedContent || status === undefined || !type) return null;

  const isPending = !status;
  const shouldShowApprove = isPending || status === "rejected";
  const shouldShowReject = isPending || status === "approved";

  const rejectPrimaryAction = {
    label: t([i18nRoot, "reject"]),
    variant: "primary" as const,
    onClick: () => handleRejectWithOptions(),
    menuOptions: rejectMenuOptions.length > 0 ? rejectMenuOptions : undefined,
  };

  const approvePrimaryAction = {
    label: t([i18nRoot, "approve"]),
    disabled: isApproving,
    onClick: () => onApprove(),
    menuOptions: approveMenuOptions.length > 0 ? approveMenuOptions : undefined,
  };

  const defaultSecondaryAction =
    shouldShowApprove && shouldShowReject
      ? {
          label: t([i18nRoot, "approve"]),
          onClick: () => onApprove(),
        }
      : undefined;

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange} size="lg">
      <Modal.Content>
        <Modal.Header
          title={getReviewTitle(type)}
          suffix={
            shouldShowGoToModerationButton && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    window.open(settingsNavPathV3.content.moderation, "_blank");
                  }}
                  startIcon="square-arrow-top-right"
                  title={t("flagged_contents.go_to_moderation")}
                >
                  {t("flagged_contents.go_to_moderation")}
                </Button>
              </div>
            )
          }
        />
        <Modal.Body>
          <div className="flex flex-col gap-6">
            <ModerationDetailContent
              flaggedContent={flaggedContent}
              onNavigate={() => handleOpenChange(false)}
            />
            {reportsCount > 0 && (
              <ReportsAccordionPanel
                flaggedContent={flaggedContent}
                flaggedContentId={flaggedContentId}
                currentReportId={currentReportId}
                currentReportFallback={flaggedContent}
                reports={reports}
                reportsCount={reportsCount}
                type={type}
              />
            )}
          </div>
          {rejectState.isActive && (
            <div className="pt-4">
              <TextArea
                rows={3}
                placeholder={t([i18nRoot, "rejection_note_placeholder"])}
                value={rejectState.reason}
                onChange={e =>
                  setRejectState(prev => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
                label={t([i18nRoot, "moderation_note"])}
              />
            </div>
          )}
        </Modal.Body>
        {rejectState.isActive ? (
          <Modal.Footer
            primaryAction={{
              label: rejectLabel,
              variant: "primary",
              disabled: isRejecting,
              onClick: handleRejectWithReason,
            }}
          />
        ) : (
          <Modal.Footer
            secondaryAction={defaultSecondaryAction}
            primaryAction={
              shouldShowReject ? rejectPrimaryAction : approvePrimaryAction
            }
          />
        )}
      </Modal.Content>
    </Modal.Root>
  );
};
